import React, { useState } from 'react';
import './Form.css';


const Form = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [touchedFields, setTouchedFields] = useState({});
  const [showMessage, setShowMessage] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields({ ...touchedFields, [name]: true });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = 'Name is required';
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^[+]?[1-9][\d]{7,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      errors.phone = 'Please enter a valid phone number (e.g., +1 (555) 123-4567)';
    }
    if (!formData.message) errors.message = 'Message is required';
    return errors;
  };

  const getFieldError = (fieldName) => {
    if (!touchedFields[fieldName]) return '';
    
    switch (fieldName) {
      case 'name':
        return !formData.name ? 'Name is required' : '';
      case 'email':
        if (!formData.email) return 'Email is required';
        if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (!formData.phone) {
          return 'Phone number is required';
        }
        if (!/^[+]?[1-9][\d]{7,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) {
          return 'Please enter a valid phone number (e.g., +1 (555) 123-4567)';
        }
        return '';
      case 'message':
        return !formData.message ? 'Message is required' : '';
      default:
        return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Form submission started');
    console.log('📝 Form data:', formData);
    
    // Mark all fields as touched to show validation errors
    setTouchedFields({
      name: true,
      email: true,
      phone: true,
      company: true,
      message: true
    });
    
    const errors = validateForm();
    console.log('✅ Validation errors:', errors);
    
    if (Object.keys(errors).length === 0) {
      try {
        console.log('📤 Form submitted successfully (backend temporarily disabled)');
        
        // Temporarily simulate success for deployment
        // TODO: Re-enable backend API calls after deployment
        
        // Clear form and show success message
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
        });
        setTouchedFields({});
        setShowError(false);
        setShowMessage(true);
        
        setTimeout(() => {
          setShowMessage(false);
        }, 5000);
        
      } catch (error) {
        console.error('❌ Form error:', error);
        setShowMessage(false);
        setErrorMessage('Something went wrong! Please try again.');
        setShowError(true);
        
        setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    } else {
      console.log('❌ Validation failed, showing alert');
      setShowMessage(false);
      
      // Create specific error message based on what's missing
      const errorMessages = [];
      if (errors.name) errorMessages.push('Name is required');
      if (errors.email) errorMessages.push('Email is required or invalid');
      if (errors.phone) errorMessages.push('Phone number is required or invalid');
      if (errors.message) errorMessages.push('Message is required');
      
      setErrorMessage(`Please fix the following: ${errorMessages.join(', ')}`);
      setShowError(true);
      
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  };

  return (
    <div className="form__section__container">
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__group">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`form__input ${
              touchedFields.name && !formData.name ? 'form__input--error' : ''
            }`}
            required
          />
          <label
            className={`form__label ${
              formData.name ? 'form__label--shrink' : ''
            }`}
          >
            Name
          </label>
          {getFieldError('name') && (
            <div className="form__field-error">
              {getFieldError('name')}
            </div>
          )}
        </div>

        <div className="form__group">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`form__input ${
              touchedFields.email && !formData.email ? 'form__input--error' : ''
            }`}
            required
          />
          <label
            className={`form__label ${
              formData.email ? 'form__label--shrink' : ''
            }`}
          >
            Email
          </label>
          {getFieldError('email') && (
            <div className="form__field-error">
              {getFieldError('email')}
            </div>
          )}
        </div>

        <div className="form__group">
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`form__input ${
              touchedFields.phone && (!formData.phone || !/^[+]?[1-9][\d]{7,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) ? 'form__input--error' : ''
            }`}
            required
          />
          <label
            className={`form__label ${
              formData.phone ? 'form__label--shrink' : ''
            }`}
          >
            Phone Number
          </label>
          {getFieldError('phone') && (
            <div className="form__field-error">
              {getFieldError('phone')}
            </div>
          )}
        </div>

        <div className="form__group">
          <input
            type="text"
            name="company"
            value={formData.company}
            onBlur={handleBlur}
            onChange={handleInputChange}
            className="form__input"
          />
          <label
            className={`form__label ${
              formData.company ? 'form__label--shrink' : ''
            }`}
          >
            Company Name
          </label>
        </div>

        <div className="form__group">
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={7}
            onBlur={handleBlur}
            className={`form__input form__textarea ${
              touchedFields.message && !formData.message
                ? 'form__input--error'
                : ''
            }`}
            required
          ></textarea>
          <label
            className={`form__label ${
              formData.message ? 'form__label--shrink' : ''
            }`}
          >
            Message
          </label>
          {getFieldError('message') && (
            <div className="form__field-error">
              {getFieldError('message')}
            </div>
          )}
        </div>

        <div className="form__submit__btn">
          <button type="submit" className="form__button">
            Send Message{' '}
            <svg
              width="20"
              height="20"
              className="form__button__svg"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.025 4.94165L17.0834 9.99998L12.025 15.0583"
                className="form__button__svg"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.91669 10H16.9417"
                className="form__button__svg"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {showMessage && (
            <div className="form__success-message">
              <div className="success__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="success__content">
                <h4>Message Sent Successfully!</h4>
                <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
              </div>
            </div>
          )}
          
          {showError && (
            <div className="form__error-message">
              <div className="error__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="error__content">
                <h4>Oops! Something went wrong</h4>
                <p>{errorMessage}</p>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default Form;
